<!-- eslint-disable vue/no-mutating-props -->
<template>
    <div>
        <div class="space-y-6">
            <div class="bg-white space-y-6 p-6 shadow-lg rounded">
                <div data-testid="email-composer-recipient-field">
                    <div
                        class="mb-4 rounded-md border border-orange-200 bg-orange-50 p-3"
                        data-testid="email-composer-historical-warning"
                        v-if="activityFeedReplyContext?.mismatch"
                    >
                        <p class="mb-2 text-sm text-orange-900">
                            {{ $t('customerProfile.activityFeed.reply.emailMismatch') }}
                        </p>
                        <div
                            class="cursor-not-allowed rounded border border-gray-200 bg-gray-100 px-3 py-2 text-gray-400"
                            aria-disabled="true"
                            data-testid="email-composer-historical-email"
                        >
                            {{ activityFeedReplyContext.historicalEmail }}
                        </div>
                    </div>

                    <div class="flex items-center space-x-2 mb-2">
                        <label class="mb-0" data-testid="email-composer-recipient-label">
                            {{
                                isForwarding
                                    ? $t('responseLeadXpress.forwardTo')
                                    : $t('responseLeadXpress.to')
                            }}
                        </label>
                        <span
                            class="text-red-500 text-sm"
                            v-text="$t('mailClient.notPreferredEmail')"
                            v-show="
                                !massMailing &&
                                preferredEmail &&
                                emails &&
                                !hasPreferredEmailSelected
                            "
                        />
                        <transition appear name="fade">
                            <email-validation
                                :email="riskyEmail"
                                :show-email="true"
                                v-if="!massMailing && riskyEmail"
                            />
                        </transition>
                    </div>
                    <div class="flex">
                        <activix-input
                            class="w-full truncate"
                            :readonly="true"
                            :value="limitText(massMailingLeadCount)"
                            v-if="massMailing"
                        />
                        <template v-else>
                            <activix-select
                                class="w-full truncate attached-r"
                                :value="response.forwardTo"
                                :placeholder="$t('options.defaultFowarding')"
                                :multiple="true"
                                :filterable="true"
                                :options="forwardToSelectionOptions"
                                :taggable="true"
                                @input="updateRecipients('forwardTo', $event)"
                                v-if="isForwarding"
                            />
                            <activix-multiselect
                                class="w-full truncate attached-r"
                                data-testid="email-composer-recipient-select"
                                label="email"
                                :allow-empty="false"
                                :disabled="disallowCommunication"
                                :limit-text="limitText"
                                :multiple="true"
                                :options="emails"
                                :searchable="false"
                                :selected="response.to"
                                :taggable="true"
                                @update="updateSelectedTo"
                                v-else
                            />
                            <activix-button
                                class="attached-l attached-r text-blue-500"
                                :active="ccFieldsVisible.cc"
                                @click="ccFieldsVisible.cc = !ccFieldsVisible.cc"
                            >
                                {{ $t('mailClient.cc') }}
                            </activix-button>
                            <activix-button
                                class="attached-l text-blue-500"
                                :active="ccFieldsVisible.bcc"
                                @click="ccFieldsVisible.bcc = !ccFieldsVisible.bcc"
                            >
                                {{ $t('mailClient.bcc') }}
                            </activix-button>
                        </template>
                    </div>
                </div>

                <div v-if="ccShouldBeVisible && !massMailing">
                    <label>{{ $t('responseLeadXpress.cc') }}</label>
                    <activix-select
                        :value="response.cc"
                        :placeholder="$t('options.emailCc')"
                        :multiple="true"
                        :filterable="true"
                        :options="ccOptions"
                        :taggable="true"
                        @input="updateRecipients('cc', $event)"
                    />
                </div>

                <div v-if="bccShouldBeVisible && !massMailing">
                    <label>{{ $t('responseLeadXpress.bcc') }}</label>
                    <activix-select
                        :value="response.bcc"
                        :placeholder="$t('options.emailBcc')"
                        :multiple="true"
                        :filterable="true"
                        :options="bccOptions"
                        :taggable="true"
                        @input="updateRecipients('bcc', $event)"
                    />
                </div>

                <div class="space-y-6 | md:space-y-0 md:flex">
                    <div class="w-full | md:w-1/4 md:pr-3">
                        <label>{{ $t('responseLeadXpress.category') }}</label>
                        <activix-multiselect
                            class="w-full truncate"
                            label="locale_name"
                            :selected="response.category"
                            :options="orderedCategories"
                            :searchable="false"
                            :allow-empty="!hasTemplate"
                            :clearable="!hasTemplate"
                            :multiple="false"
                            :disabled="disallowCommunication"
                            @update="updateSelectedCategory"
                            @cancel="() => updateSelectedCategory('')"
                        />
                    </div>
                    <div class="w-full | md:flex-1 md:px-3">
                        <label>
                            {{ $t('responseLeadXpress.template') }}
                            <span class="text-red-500" v-if="hasCategory && !hasTemplate">*</span>
                        </label>
                        <div class="flex items-center">
                            <div class="flex-1">
                                <activix-multiselect
                                    class="w-full truncate attached-r"
                                    label="title"
                                    :selected="response.template"
                                    :options="filteredTemplates"
                                    :searchable="true"
                                    :allow-empty="false"
                                    :multiple="false"
                                    :disabled="disabledTemplateSelection || templateLoading"
                                    :max-height="400"
                                    @update="updateSelectedTemplate"
                                />
                            </div>

                            <div class="flex" v-if="!massMailing">
                                <activix-button
                                    class="attached-l attached-r text-blue-500"
                                    data-testid="email-composer-language-fr-button"
                                    :active="currentLang == 'FR'"
                                    @click="$emit('change-lang', 'FR')"
                                >
                                    {{ 'FR' }}
                                </activix-button>
                                <activix-button
                                    class="attached-l text-blue-500"
                                    data-testid="email-composer-language-en-button"
                                    :active="currentLang == 'EN'"
                                    @click="$emit('change-lang', 'EN')"
                                >
                                    {{ 'EN' }}
                                </activix-button>
                            </div>
                            <div class="mx-2" v-if="templateLoading">
                                <activix-spinner :size="20" />
                            </div>
                        </div>
                    </div>
                    <div class="w-full | md:w-1/4 md:pl-3">
                        <label>{{ $t('responseLeadXpress.civility') }}</label>
                        <activix-multiselect
                            label="title"
                            class="w-full truncate"
                            :selected="civility"
                            :options="civilities"
                            :searchable="false"
                            :allow-empty="false"
                            :multiple="false"
                            :disabled="disabledCivilitySelection"
                            :max-height="800"
                            @update="updateSelectedCivility"
                        />
                    </div>
                </div>

                <div class="space-y-6 | xs:space-y-0 xs:flex xs:justify-between">
                    <div class="w-full | xs:w-2/3 | md:w-1/2">
                        <lead-xpress-from
                            :user="currentUser"
                            :lead="lead"
                            :campaign-settings="campaignSettings"
                            v-model:from="response.from"
                            v-model:from-backup="response.fromBackup"
                            v-if="showFrom"
                        />
                        <div class="flex items-center space-x-6 | xs:h-10" v-else-if="!massMailing">
                            <activix-tooltip
                                :content="$t('responseLeadXpress.originalCommunicationTooltip')"
                                v-if="!includeOriginalCommunicationDisabled"
                            >
                                <activix-checkbox
                                    :disabled="includeOriginalCommunicationDisabled"
                                    v-model="includeOriginalCommunication"
                                >
                                    <span class="m-0">{{
                                        $t('responseLeadXpress.originalCommunication')
                                    }}</span>
                                </activix-checkbox>
                            </activix-tooltip>
                            <activix-tooltip
                                :content="$t('responseLeadXpress.addLastEmailBodyTooltip')"
                            >
                                <activix-checkbox
                                    :value="addLastEmailBody"
                                    @input="$emit('update:addLastEmailBody', $event)"
                                >
                                    <span class="m-0">{{
                                        $t('responseLeadXpress.addLastEmailBody')
                                    }}</span>
                                </activix-checkbox>
                            </activix-tooltip>
                        </div>
                    </div>

                    <div class="w-full pl-6 | xs:w-2/3 | md:w-1/2">
                        <div v-if="massMailing">
                            <label>{{ $t('responseLeadXpress.language') }}</label>
                            <activix-multiselect
                                identifier="key"
                                label="label"
                                :allow-empty="false"
                                :multiple="false"
                                :options="localeOptions"
                                :searchable="false"
                                :placeholder="$t('email.lead_locale')"
                                :selected="selectedLocalePreference"
                                @update="updateSelectedLocale"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-6">
                <activix-tabs
                    :tabs="tabs"
                    text-alignment="left"
                    v-model="activeTab"
                    v-show="massMailing"
                />
                <activix-alert type="info" v-if="signatureWillBeUpdatedOnSend">
                    {{ $t('responseLeadXpress.signatureWillBeUpdatedOnSend') }}
                </activix-alert>

                <template v-if="massMailing || activeTab === 'fr'">
                    <lead-xpress-translate
                        locale="fr"
                        :subject="response.subject.fr"
                        :design="response.design?.fr || response.template?.design?.fr"
                        :body="response.body.fr"
                        :attachments="response.attachments?.fr"
                        :is-advanced="templateAdvanced"
                        :template="response.template"
                        :lead="massMailing ? null : lead"
                        :sender="sender"
                        :video="video"
                        :history="lastEmailBody"
                        :add-last-email-body="addLastEmailBody"
                        :original-communication="originalCommunication"
                        :ai-draft-state="aiDraftState"
                        :ai-draft-feedback="aiDraftFeedback"
                        :ai-draft-enabled="aiDraftEnabled"
                        :ai-draft-generated-draft="aiDraftGeneratedDraft"
                        :ai-draft-has-generated-draft="aiDraftHasGeneratedDraft"
                        :ai-draft-rate-limit-seconds="aiDraftRateLimitSeconds"
                        :ai-draft-service-lead-disabled="aiDraftServiceLeadDisabled"
                        :ai-draft-can-undo="aiDraftCanUndoByLocale?.fr ?? false"
                        :can-generate-ai-draft="canGenerateAiDraftByLocale?.fr ?? false"
                        :can-update-ai-draft="canUpdateAiDraftByLocale?.fr ?? false"
                        :ai-draft-update-disabled-reason="
                            aiDraftUpdateDisabledReasonByLocale?.fr ?? null
                        "
                        @generate-ai-draft="(...args) => $emit('generate-ai-draft', ...args)"
                        @update-ai-draft="(...args) => $emit('update-ai-draft', ...args)"
                        @undo-ai-draft="(...args) => $emit('undo-ai-draft', ...args)"
                        @stop-ai-draft="$emit('stop-ai-draft')"
                        @select-ai-draft-feedback="$emit('select-ai-draft-feedback', $event)"
                        @submit-ai-draft-feedback="$emit('submit-ai-draft-feedback', $event)"
                        @update-subject="response.subject.fr = $event"
                        @update-body="response.body.fr = $event"
                        @update-design="response.design.fr = $event"
                        @update-attachment="response.attachments.fr = $event"
                        @video-cancelled="$emit('videoCancelled', $event)"
                        v-show="activeTab == 'fr'"
                    />
                </template>

                <template v-if="massMailing || activeTab === 'en'">
                    <lead-xpress-translate
                        locale="en"
                        :subject="response.subject.en"
                        :design="response.design?.en || response.template?.design?.en"
                        :body="response.body.en"
                        :attachments="response.attachments?.en"
                        :is-advanced="response.template?.is_advanced ?? false"
                        :lead="massMailing ? null : lead"
                        :template="response.template"
                        :sender="sender"
                        :video="video"
                        :history="lastEmailBody"
                        :add-last-email-body="addLastEmailBody"
                        :original-communication="originalCommunication"
                        :ai-draft-state="aiDraftState"
                        :ai-draft-feedback="aiDraftFeedback"
                        :ai-draft-enabled="aiDraftEnabled"
                        :ai-draft-generated-draft="aiDraftGeneratedDraft"
                        :ai-draft-has-generated-draft="aiDraftHasGeneratedDraft"
                        :ai-draft-rate-limit-seconds="aiDraftRateLimitSeconds"
                        :ai-draft-service-lead-disabled="aiDraftServiceLeadDisabled"
                        :ai-draft-can-undo="aiDraftCanUndoByLocale?.en ?? false"
                        :can-generate-ai-draft="canGenerateAiDraftByLocale?.en ?? false"
                        :can-update-ai-draft="canUpdateAiDraftByLocale?.en ?? false"
                        :ai-draft-update-disabled-reason="
                            aiDraftUpdateDisabledReasonByLocale?.en ?? null
                        "
                        @generate-ai-draft="(...args) => $emit('generate-ai-draft', ...args)"
                        @update-ai-draft="(...args) => $emit('update-ai-draft', ...args)"
                        @undo-ai-draft="(...args) => $emit('undo-ai-draft', ...args)"
                        @stop-ai-draft="$emit('stop-ai-draft')"
                        @select-ai-draft-feedback="$emit('select-ai-draft-feedback', $event)"
                        @submit-ai-draft-feedback="$emit('submit-ai-draft-feedback', $event)"
                        @update-subject="response.subject.en = $event"
                        @update-body="response.body.en = $event"
                        @update-design="response.design.en = $event"
                        @update-attachment="response.attachments.en = $event"
                        @video-cancelled="$emit('videoCancelled', $event)"
                        v-show="activeTab == 'en'"
                    />
                </template>
            </div>
        </div>

        <div
            class="bg-white space-y-6 p-6 shadow-lg rounded mt-8"
            v-if="lead && connectedCommunicationsCount && !massMailing"
        >
            <email-accordion
                :communications="lead.communications"
                :lead-full-name="lead.fullName"
                :show-more="true"
            />
        </div>

        <div class="bg-white p-6 mt-8 rounded-lg shadow-lg" v-if="showOriginalEmail">
            <activix-label :label="$t('email.originalEmail')" />
            <div class="mt-3">
                <span v-html="lead.chat" v-if="lead.chat" />
                <span v-else>
                    <span v-html="scraperDescription" v-if="lead.scraper_description" />
                </span>
            </div>
        </div>

        <activix-confirm-modal
            type="danger"
            :content="$t('templates.askDeleteTemplate')"
            @approve="deleteTemplate(modals.askDeleteTemplate.locale)"
            v-model:opened="modals.askDeleteTemplate.opened"
        />
    </div>
</template>

<script>
    import { getCapabilityContentLanguages } from '@/locales/supportedLocales.ts';

    /* eslint-disable vue/require-prop-types, vue/no-mutating-props, no-unsafe-optional-chaining */
    import { camelCase, get, orderBy } from 'lodash-es';
    import { ActivixSelect } from '@/components/activix-ui/index.ts';
    import { nl2br } from '@/utils/string.js';
    import LeadEmail from '@/entities/LeadEmail.js';
    import EmailValidationRisk from '@/entities/EmailValidationRisk.js';
    import EmailCompose from '@/mixins/EmailCompose.js';
    import LeadXpressFrom from '@/components/lead_xpress/LeadXpressFrom.vue';
    import EmailAccordion from '@/components/lead/EmailAccordion.vue';
    import EmailValidation from '@/components/lead/EmailValidation.vue';

    // Pinia
    import { mapActions, mapState } from 'pinia';
    import TemplateType from '@/entities/TemplateType.js';
    import { showWarning } from '@/utils/toastr.js';
    import ActivixLabel from '@/components/elements/ActivixLabel.vue';
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { useUserStore } from '@/store/modules/user/store.js';
    import { useDashboardStore } from '@/store/modules/dashboard/store.js';
    import { useGlobalStore } from '@/store/store.ts';
    import { replaceSignature as replaceSignatureHelper } from '@/components/lead_xpress/utils/signatureReplacement.js';
    import {
        filterActivityFeedEmailRecipients,
        filterEmailRecipientsByAllowedAddresses,
    } from '@/components/customerProfile/activityFeedEmailRecipients.ts';
    import LeadXpressTranslate from './LeadXpressTranslate.vue';

    export default {
        compatConfig: { WATCH_ARRAY: false },

        emits: [
            'change-lang',
            'update:addLastEmailBody',
            'update:originalCommunication',
            'change-locale',
            'set-initial-body',
            'update:response',
            'generate-ai-draft',
            'update-ai-draft',
            'undo-ai-draft',
            'stop-ai-draft',
            'select-ai-draft-feedback',
            'submit-ai-draft-feedback',
            'videoCancelled',
        ],

        components: {
            ActivixSelect,
            LeadXpressTranslate,
            LeadXpressFrom,
            EmailAccordion,
            EmailValidation,
            ActivixLabel,
        },

        mixins: [EmailCompose],

        props: [
            'communicating',
            'communicatingUser',
            'currentLang',
            'draft',
            'isForwarding',
            'lastEmailBody',
            'lead',
            'response',
            'sender',
            'signaturesByLocale',
            'signatureRemovedByUser',
            'unsubscribeLink',
            'massMailingLeadCount',
            'addLastEmailBody',
            'responseTemplates',
            'campaignSettings',
            'aiDraftState',
            'aiDraftFeedback',
            'aiDraftEnabled',
            'aiDraftGeneratedDraft',
            'aiDraftHasGeneratedDraft',
            'aiDraftRateLimitSeconds',
            'aiDraftServiceLeadDisabled',
            'aiDraftCanUndoByLocale',
            'canGenerateAiDraftByLocale',
            'canUpdateAiDraftByLocale',
            'aiDraftUpdateDisabledReasonByLocale',
            'activityFeedReplyContext',
            'customerProfileOutput',
            'customerProfileEmailContext',
        ],

        data() {
            return {
                hasResponseObject: false,
                civility: {
                    id: 'none',
                    title: this.$t('general.none'),
                },
                showCivility: false,
                attachementInternalErrors: [],
                includeOriginalCommunication: false,
                options: [],
                forwardToOptions: [],
                ccFieldsVisible: {
                    cc: false,
                    bcc: false,
                },
                activeTab: '',
                localeAvailable: ['en', 'fr'],
                template: {
                    type: TemplateType.EMAIL,
                    is_advanced: false,
                    title: '',
                    category: null,
                    cc: [],
                    bcc: [],
                    ccAssigned: false,
                    bccAssigned: false,
                    attachments: [],
                    automation_actions: [],
                },
                modals: {
                    askDeleteTemplate: {
                        opened: false,
                        locale: '',
                        subject: {
                            fr: '',
                            en: '',
                        },
                        body: {
                            fr: '',
                            en: '',
                        },
                        design: {
                            fr: '',
                            en: '',
                        },
                        title: '',
                        attachments: '',
                    },
                },
                localePreference: 'leadLocale',
                localeOptions: [
                    {
                        key: 'leadLocale',
                        label: this.$t('email.lead_locale'),
                    },
                ],
                templateLoading: false,
            };
        },

        computed: {
            ...mapState(useDashboardStore, {
                massMailing: 'isMassMailing',
                pagination: 'pagination',
            }),
            ...mapState(useGlobalStore, {
                attachmentParameters: 'attachmentParameters',
            }),

            parentAuthUser() {
                return useUserStore().parentAuthUser;
            },

            authUser() {
                return useUserStore().authUser;
            },

            contextAccount() {
                return useAccountStore().contextAccount;
            },

            users() {
                return this.contextAccount.users;
            },

            ownCommunication() {
                return (
                    this.communicatingUser.id === this.parentAuthUser?.id ||
                    this.communicatingUser.id === this.authUser.id
                );
            },

            disallowCommunication() {
                return this.communicating && !this.ownCommunication;
            },

            ziggeoId() {
                return this.draft?.video?.ziggeo_id;
            },

            videoProcessed() {
                return this.draft?.video?.video_processed;
            },

            video() {
                return {
                    draftZiggeoId: this.ziggeoId,
                    draftProcessed: this.videoProcessed,
                    ziggeoId: this.response.ziggeoId,
                    user: this.currentUser,
                    leadId: this.leadId,
                };
            },

            fakeSignature() {
                return `
                    <div class="flex flex-col">
                        <div class="h-4 w-32 rounded bg-gray-200 mb-2"></div>
                        <div class="h-20 w-96 rounded bg-gray-200"></div>
                    </div>
                `;
            },

            showOriginalEmail() {
                return (
                    this.lead &&
                    !this.massMailing &&
                    (this.lead.chat || this.lead.scraper_description || this.lead.original_email)
                );
            },

            selectedLocalePreference() {
                return this.localeOptions.find((locale) => locale.key === this.localePreference);
            },

            displayCommunicationVideo() {
                const hasVideoData = Object.values(this.response.body).some((value) => {
                    if (typeof value === 'string') {
                        return (
                            value.includes('<data value="data-video-communication-thumbnail"') ||
                            value.includes('<thumbnail data-video-communication-thumbnail')
                        );
                    }
                    return false;
                });

                return !this.templateAdvanced || hasVideoData;
            },

            hasCategory() {
                return !!this.response.category?.id;
            },

            hasTemplate() {
                return !!this.response.template?.id;
            },

            templateAdvanced() {
                return (
                    (this.response.template?.is_advanced &&
                        (!!this.response.template?.design?.en ||
                            !!this.response.template?.design?.fr)) ||
                    !!this.response.design?.fr ||
                    !!this.response.design?.en
                );
            },

            categories() {
                return this.customCategories.filter((category) => {
                    return this.responseTemplates.some(
                        (template) => template.response_template_custom_category_id === category.id
                    );
                });
            },

            customCategories() {
                return useAccountStore().config.customCategories.map((category) => ({
                    id: category.id,
                    locale_name: this.getCategoryLocaleName(category),
                }));
            },

            orderedCategories() {
                return orderBy(this.categories, 'locale_name');
            },

            ccShouldBeVisible() {
                return !empty(this.response.cc) || this.ccFieldsVisible.cc;
            },

            bccShouldBeVisible() {
                return !empty(this.response.bcc) || this.ccFieldsVisible.bcc;
            },

            civilities() {
                return [
                    {
                        id: 'none',
                        title: this.$t('general.none', this.currentLang.toLowerCase()),
                    },
                    {
                        id: 'mr',
                        title: this.$t('database.mr', this.currentLang.toLowerCase()),
                    },
                    {
                        id: 'ms',
                        title: this.$t('database.ms', this.currentLang.toLowerCase()),
                    },
                    {
                        id: 'mrs',
                        title: this.$t('database.mrs', this.currentLang.toLowerCase()),
                    },
                    {
                        id: 'miss',
                        title: this.$t('database.miss', this.currentLang.toLowerCase()),
                    },
                    {
                        id: 'dr',
                        title: this.$t('database.dr', this.currentLang.toLowerCase()),
                    },
                    {
                        id: 'me',
                        title: this.$t('database.me', this.currentLang.toLowerCase()),
                    },
                ].filter(
                    (item, index, civilities) =>
                        civilities.findIndex((civility) => civility.title === item.title) === index
                );
            },

            defaultTemplate() {
                return this.responseTemplates.find((template) => template.is_default);
            },

            filteredTemplates() {
                if (!this.response.category?.id) {
                    return [];
                }

                const responseTemplates = this.responseTemplates
                    .filter(
                        (responseTemplate) =>
                            responseTemplate.type === TemplateType.EMAIL &&
                            responseTemplate.response_template_custom_category_id ===
                                this.response.category.id &&
                            responseTemplate.is_active &&
                            (this.massMailing
                                ? true
                                : responseTemplate.translations.some(
                                      (template) =>
                                          template.locale === this.currentLang.toLowerCase()
                                  ))
                    )
                    .map((value) => {
                        const translationFr =
                            value.translations.find((t) => t.locale == 'fr') ?? '';
                        const translationEn =
                            value.translations.find((t) => t.locale == 'en') ?? '';

                        const titlePrefix =
                            value.account_id != this.contextAccount.id
                                ? `[${this.$t('leadXpress.parent')}]`
                                : '';
                        let title = value.title || translationFr.title || translationEn.title;

                        if (this.currentLang.toLowerCase() === 'en') {
                            title = value.title || translationEn.title || translationFr.title;
                        }

                        return {
                            id: value.id,
                            title: `${titlePrefix} ${title}`.trim(),
                            type: value.type,
                            is_active: value.is_active,
                            is_favorite: value.is_favorite,
                            response_template_custom_category_id:
                                value.response_template_custom_category_id,
                        };
                    });

                return orderBy(responseTemplates, ['title'], ['asc']).sort(
                    (a, b) => b.is_favorite - a.is_favorite
                );
            },

            emails() {
                if (empty(this.lead)) {
                    return [];
                }

                if (this.customerProfileEmailContext) {
                    const selectedIds = new Set(
                        this.response.to.map((email) => email.contact_email_id)
                    );
                    return this.customerProfileEmailContext.recipients.map((recipient) => ({
                        id: recipient.contact_email_id,
                        contact_email_id: recipient.contact_email_id,
                        contact_id: recipient.contact_id,
                        email: recipient.destination,
                        valid: true,
                        preferred: selectedIds.has(recipient.contact_email_id),
                        validated_email: {
                            is_disposable: false,
                            risk_score: 0,
                        },
                    }));
                }

                if (this.customerProfileOutput?.recipient) {
                    return [this.customerProfileOutput.recipient];
                }

                const eligibleEmails = this.activityFeedReplyContext
                    ? filterActivityFeedEmailRecipients(
                          this.lead.lead_emails,
                          this.activityFeedReplyContext.allowedEmails
                      )
                    : this.lead.lead_emails.filter((tmpEmail) => {
                          return (
                              tmpEmail.valid &&
                              !tmpEmail.validated_email?.is_disposable &&
                              !tmpEmail.email_sender_suppression_for_current_user
                          );
                      });

                return eligibleEmails
                    .map((tmpEmail) => {
                        if (tmpEmail.preferred) {
                            tmpEmail.icon = 'bold/rating-star';
                            tmpEmail.iconStyle = 'text-blue-500';
                        }

                        return tmpEmail;
                    })
                    .sort((a, b) => a.validated_email?.risk_score - b.validated_email?.risk_score);
            },

            riskyEmail() {
                const email = this.response.to
                    .filter((email) => email.validated_email?.risk === EmailValidationRisk.MEDIUM)
                    .sort((a, b) => a.validated_email?.risk_score - b.validated_email?.risk_score)
                    .reverse()[0];

                if (!email) {
                    return null;
                }

                return new LeadEmail(email);
            },

            preferredEmail() {
                return this.emails.find((email) => email.preferred);
            },

            hasPreferredEmailSelected() {
                return this.response.to.some((email) => email.preferred);
            },

            connectedCommunicationsCount() {
                let count = 0;

                if (this.lead.communications.length > 0) {
                    const communications = this.lead.communications.filter((communication) => {
                        if (
                            communication.connected &&
                            communication.service_id == 4 &&
                            communication.status != 'interrupted' &&
                            communication.status != 'error' &&
                            communication.status != 'invalid' &&
                            communication.status != 'pending'
                        ) {
                            return true;
                        }

                        return false;
                    });

                    count = communications.length;
                }

                return count;
            },

            scraperDescription() {
                let content = '';

                if (!empty(this.lead.scraper_description)) {
                    content = nl2br(this.lead.scraper_description);

                    const emailRegex = /(\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*)/;
                    const phoneRegex = /((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}/;

                    if (this.authUser.hasAdvisorSkills()) {
                        content = content.replace(emailRegex, 'XXXX');
                        content = content.replace(phoneRegex, 'XXXX');
                    }
                }

                return content;
            },

            currentCivility() {
                let tmpCivility = 'ms';

                if (
                    (empty(this.lead.civility) || this.lead.civility == -1) &&
                    (empty(this.lead.sex) || this.lead.civility == -1)
                ) {
                    return {
                        id: 'none',
                        title: this.$t('general.none', this.currentLang.toLowerCase()),
                    };
                }
                if (!empty(this.lead.civility)) {
                    tmpCivility = this.lead.civility;
                } else if (!empty(this.lead.sex) && this.lead.sex == 'M') {
                    tmpCivility = 'mr';
                }

                return {
                    id: tmpCivility,
                    title: this.$t(`database.${tmpCivility}`, this.currentLang.toLowerCase()),
                };
            },

            originalCommunication() {
                let originalCommunication = '';

                if (
                    this.includeOriginalCommunicationDisabled ||
                    !this.includeOriginalCommunication
                ) {
                    return originalCommunication;
                }

                originalCommunication = `<hr>${this.$t('email.originalEmail')}:<br><br>${
                    this.lead.comment
                }<hr>`;

                return nl2br(originalCommunication);
            },

            includeOriginalCommunicationDisabled() {
                return (
                    !this.lead.comment || this.massMailing || this.response.template?.is_advanced
                );
            },

            disabledTemplateSelection() {
                return (
                    !this.response.category?.id ||
                    !this.filteredTemplates.length ||
                    !this.templateAttributes.length ||
                    this.disallowCommunication
                );
            },

            disabledCivilitySelection() {
                return (
                    !this.showCivility ||
                    !this.response.category?.id ||
                    !this.filteredTemplates.length ||
                    !this.templateAttributes.length ||
                    this.disallowCommunication
                );
            },

            signatureWillBeUpdatedOnSend() {
                return !this.sender?.id;
            },

            showFrom() {
                return this.massMailing;
            },

            currentUser() {
                return (
                    this.parentAuthUser.childUserForAccount(this.lead.account_id) || this.authUser
                );
            },

            leadId() {
                return this.lead?.id;
            },

            tabs() {
                const tabsArray = this.localeCodes.map((locale) => ({
                    name: locale,
                    label: this.$t(`locales.locales.${locale}.name`),
                    subLabel: this.$t(`locales.locales.${locale}.realName`),
                }));

                if (this.currentLang) {
                    const currentLanguage = this.currentLang.toLowerCase();
                    tabsArray.sort(
                        (first, second) =>
                            Number(second.name === currentLanguage) -
                            Number(first.name === currentLanguage)
                    );
                }

                if (tabsArray.length) {
                    this.updateActiveTabs(tabsArray[0].name);
                }

                return tabsArray;
            },

            activeUsersEmail() {
                return [
                    ...new Set(
                        this.users
                            .filter((user) => user.active)
                            .map((user) => user.email)
                            .filter((email) => typeof email === 'string' && email.trim().length)
                    ),
                ];
            },

            forwardToSelectionOptions() {
                return this.buildEmailOptions(this.response.forwardTo, this.forwardToOptions);
            },

            ccOptions() {
                return this.buildEmailOptions(this.response.cc, this.activeUsersEmailOptions);
            },

            bccOptions() {
                return this.buildEmailOptions(this.response.bcc, this.activeUsersEmailOptions);
            },

            activeUsersEmailOptions() {
                return this.activeUsersEmail
                    .filter((email) => typeof email === 'string' && email.trim().length)
                    .map((email) => ({
                        value: email,
                        label: email,
                    }));
            },

            localeCodes() {
                return getCapabilityContentLanguages(this.contextAccount.localization, 'email').map(
                    (locale) => locale.code
                );
            },
        },

        watch: {
            localeCodes: {
                immediate: true,
                handler() {
                    const localeOptions = getCapabilityContentLanguages(
                        this.contextAccount.localization,
                        'email'
                    ).map((locale) => ({
                        key: locale.code,
                        label: this.$t(`locales.locales.${locale.code}.name`),
                    }));

                    localeOptions.push({
                        key: 'leadLocale',
                        label: this.$t('email.lead_locale'),
                    });

                    this.localeOptions = localeOptions;
                },
            },

            response(newValue) {
                if (!empty(newValue.cc)) {
                    this.ccFieldsVisible.cc = true;
                }

                if (!empty(newValue.bcc)) {
                    this.ccFieldsVisible.bcc = true;
                }
            },

            civility(newValue) {
                this.response.civility = newValue;

                for (const locale of this.localeCodes) {
                    const html = $('<div />', {
                        html: this.response.body[locale],
                    });

                    html.find('.civility').html(this.getCivilityPrefix(locale, newValue.id));

                    this.response.body[locale] = html.html();
                    this.updateResponse();
                }

                const civilityId = newValue.id != 'none' ? newValue.id : null;
                this.updateLeadAction(this.lead.id, { civility: civilityId });
            },

            signaturesByLocale(newValue, oldValue) {
                if (newValue?.fr === oldValue?.fr && newValue?.en === oldValue?.en) {
                    return;
                }

                this.replaceSignatureInBodies(newValue, oldValue);
            },

            unsubscribeLink(newValue) {
                for (const locale of this.localeCodes) {
                    const html = $('<div />', {
                        html: this.response.body[locale],
                    });

                    html.find('[data-composer-unsubscribe="true"], .unsubscribe-link')
                        .first()
                        .replaceWith(newValue);

                    this.response.body[locale] = html.html();
                    this.updateResponse();
                }
            },

            'response.to'(newValue) {
                if (
                    !newValue.length &&
                    this.emails.length == 1 &&
                    !this.activityFeedReplyContext?.mismatch
                ) {
                    this.response.to = this.emails;
                    this.updateResponse();
                }
            },

            'response.subject'(newValue) {
                if (!newValue.length) {
                    this.updateResponse();
                }
            },

            'response.body'(newValue) {
                if (!newValue.length) {
                    this.updateResponse();
                }
            },

            'response.design'() {
                this.updateResponse();
            },

            async 'response.template'(newValue) {
                this.showCivility = false;

                if (!empty(newValue)) {
                    this.showCivility = true;

                    this.civility = this.currentCivility;

                    if (newValue.translations) {
                        await Promise.all(
                            newValue.translations.map(async (translation) => {
                                const locale = translation.locale;
                                const payload = {
                                    sender: this.massMailing
                                        ? this.sender
                                        : this.sender || this.authUser,
                                    lead: this.massMailing ? null : this.lead,
                                    locale,
                                    prefix: this.getCivilityPrefix(locale),
                                    massMailing: this.massMailing,
                                    users: this.users,
                                    customerProfileOutput: this.customerProfileOutput,
                                };
                                const body = await this.replaceAttributesInMessage({
                                    ...payload,
                                    html: true,
                                    message: translation.body,
                                });
                                const subject = await this.replaceAttributesInMessage({
                                    ...payload,
                                    html: false,
                                    message: translation.subject,
                                });

                                if (!this.hasResponseObject) {
                                    this.response.subject[locale] = subject;
                                }

                                if (newValue.is_advanced) {
                                    this.response.body[locale] = body;
                                } else {
                                    const html = $('<div />', {
                                        html: this.response.body[locale],
                                    });
                                    const bodyNode = html.find('.body').first();

                                    if (bodyNode.length) {
                                        bodyNode.html(body);
                                        this.response.body[locale] = html.html();
                                    } else {
                                        this.response.body[locale] =
                                            this.appendExistingComposerSuffix(
                                                body,
                                                this.response.body[locale]
                                            );
                                    }
                                }

                                this.response.design[locale] = newValue.is_advanced
                                    ? translation.design
                                    : null;

                                this.updateResponse();
                            })
                        );
                    }
                }
            },

            'response.templateAttachments'() {
                this.updateResponse();
            },

            'response.attachments'() {
                this.updateResponse();
            },

            includeOriginalCommunication() {
                this.$emit('update:originalCommunication', this.originalCommunication);
                this.updateResponse();
            },
        },

        methods: {
            ...mapActions(useGlobalStore, ['updateLeadAction']),

            limitText(count) {
                return this.$t('responseLeadXpress.selectedRecipient', count);
            },

            getCategoryLocaleName(category) {
                if (!category.account_id) {
                    return this.$t(`divisions.${camelCase(category.name)}`);
                }

                if (this.contextAccount.id !== category.account_id) {
                    return `[Parent] ${category.name}`;
                }

                return category.name;
            },

            updateRecipients(field, value) {
                const emails = this.splitEmailList(value);

                if (emails.some((email) => !this.emailIsValid(email))) {
                    showWarning(this.$t('client.wrongEmail'));
                }

                const validEmails = this.cleanEmailList(emails);

                this.response[field] = [...new Set(validEmails)];

                this.updateResponse();
            },

            splitEmailList(emails) {
                return emails.map((email) => email.toLowerCase().split(/[\s,;]+/)).flat();
            },

            cleanEmailList(emails) {
                return emails.filter((email) => this.emailIsValid(email)) || [];
            },

            buildEmailOptions(values = [], baseOptions = []) {
                const extraOptions = values.map((value) => ({
                    value,
                    label: value,
                }));

                return [...baseOptions, ...extraOptions].filter((option, index, options) => {
                    return (
                        options.findIndex((candidate) => candidate.value === option.value) === index
                    );
                });
            },

            updateSelectedCategory(value) {
                this.response.category = value;
                this.response.template = null;

                this.updateResponse();
            },

            updateSelectedLocale(value) {
                this.localePreference = get(value, 'key', null);

                if (this.localePreference !== 'leadLocale') {
                    this.activeTab = this.localePreference;
                }

                this.$emit('change-locale', this.localePreference);
            },

            updateActiveTabs(locale) {
                this.activeTab = locale;
            },

            replaceSignatureInBodies(signaturesByLocale, previousSignaturesByLocale = {}) {
                for (const locale of this.localeCodes) {
                    this.response.body[locale] = replaceSignatureHelper({
                        body: this.response.body[locale],
                        newSignature: signaturesByLocale?.[locale] || '',
                        oldSignature: previousSignaturesByLocale?.[locale] || '',
                        locale,
                        users: this.users,
                        currentUser: this.currentUser,
                        authUser: this.authUser,
                        skipInsertionWhenAbsent: this.signatureRemovedByUser?.[locale] || false,
                    });

                    this.updateResponse();
                }
            },

            appendExistingComposerSuffix(body, previousBody) {
                const suffixPatterns = [
                    '<div class="signature" data-composer-signature="true">',
                    'data-composer-signature="true"',
                    '<div class="signature"',
                    '<div data-composer-separator="true">',
                    '<div class="unsubscribe-link" data-composer-unsubscribe="true"',
                    'data-composer-unsubscribe="true"',
                    '<div class="unsubscribe-link"',
                    '<div class="original-communication" data-composer-original-communication="true">',
                    'data-composer-original-communication="true"',
                    '<div class="original-communication"',
                ];
                const suffixIndex = suffixPatterns
                    .map((pattern) => previousBody.indexOf(pattern))
                    .filter((index) => index !== -1)
                    .sort((left, right) => left - right)[0];

                if (suffixIndex === undefined) {
                    return body;
                }

                return `${body}${previousBody.slice(suffixIndex)}`;
            },

            async updateSelectedTemplate(value) {
                this.$emit('set-initial-body');
                this.templateLoading = true;

                const responseTemplate = await this.fetchSelectedTemplate(value);

                this.response.template = responseTemplate;
                this.response.templateAttachments = responseTemplate.attachments;

                const emails = await this.lead.getAssignedUserEmails();

                if (responseTemplate.cc_assigned && emails.length > 0) {
                    const validCc = (responseTemplate.cc || []).filter((email) =>
                        emails.includes(email)
                    );

                    if (validCc) {
                        this.response.cc = this.response.cc.concat(validCc);
                    }
                } else {
                    this.response.cc = this.response.cc.concat(responseTemplate.cc || []);
                }

                if (responseTemplate.bcc_assigned && emails.length > 0) {
                    const validBcc = (responseTemplate.bcc || []).filter((email) =>
                        emails.includes(email)
                    );

                    if (validBcc) {
                        this.response.bcc = this.response.bcc.concat(validBcc);
                    }
                } else {
                    this.response.bcc = this.response.bcc.concat(responseTemplate.bcc || []);
                }

                const attachments = {
                    en: [],
                    fr: [],
                };

                responseTemplate.translations.forEach((translation) => {
                    const locale = translation.locale;
                    const attachmentArray = attachments[locale];

                    translation.attachments.forEach((attachment) => {
                        attachmentArray.push({
                            id: attachment.id,
                            name: attachment.name,
                            size: attachment.size,
                            path: attachment.path,
                        });
                    });
                });

                this.response.attachments = attachments;

                this.updateResponse();
            },

            updateSelectedTo(value) {
                if (!this.activityFeedReplyContext || this.customerProfileEmailContext) {
                    this.response.to = value;
                    this.updateResponse();
                    return;
                }

                this.response.to = filterEmailRecipientsByAllowedAddresses(
                    value,
                    this.activityFeedReplyContext.allowedEmails
                );
                this.updateResponse();
            },

            updateSelectedCivility(value) {
                this.civility = value;
            },

            getCivilityPrefix(
                locale = this.lead?.locale || this.currentLang.toLowerCase(),
                civilityId = this.currentCivility.id
            ) {
                if (civilityId === 'none') {
                    return '';
                }

                return this.$t(`database.${civilityId}`, {}, { locale });
            },

            updateResponse() {
                this.$emit('update:response', this.response);
            },

            async fetchSelectedTemplate(value) {
                this.templateLoading = true;

                try {
                    const options = {};
                    if (this.contextAccount && this.contextAccount.id != null) {
                        options.accountId = this.contextAccount.id;
                    }
                    return await this.$api.templates.show(value.id, options);
                } catch (error) {
                    this.$notify.warning(this.$t('responseLeadXpress.templateNotFound'));
                    return value;
                } finally {
                    this.templateLoading = false;
                }
            },

            async updateDefault() {
                if (this.defaultTemplate) {
                    const category = this.customCategories.find(
                        (category) =>
                            category.id ===
                            this.defaultTemplate.response_template_custom_category_id
                    );

                    if (category) {
                        this.updateSelectedCategory(category);
                    }

                    const defaultTemplate = this.filteredTemplates.find(
                        (template) => template.id === this.defaultTemplate.id
                    );

                    if (defaultTemplate) {
                        this.updateSelectedTemplate(defaultTemplate);
                    }
                }

                if (this.preferredEmail) {
                    this.updateSelectedTo([this.preferredEmail]);
                }
            },

            askDeleteTemplate(locale) {
                this.modals.askDeleteTemplate.locale = locale;
                this.modals.askDeleteTemplate.opened = true;
            },
        },
    };
</script>
